import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { loadScriptByURL, queryStringToObject, SITE_KEY, validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// SVG
import { ReactComponent as Twofa } from '../../assets/svg/twoFA.svg';

// TYPES
import { AuthenticationValidateTwoFactorCommand } from '../../api/models';

interface twoFactorAuthenticationTypes {}

const TwoFactorAuthentication: React.FC<twoFactorAuthenticationTypes> = ({}) => {
	const dispatch = useAppDispatch();
	const location = useLocation();

	const [isLoading, setLoading] = React.useState<boolean>(true);
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	let history = useHistory();
	const [params, setParams] = React.useState<{ [key: string]: any }>();

	const initialValues = {
		token: ''
	};

	const validationSchema = Yup.object({
		token: Yup.string().required('Required')
	});

	const checkLocationParams = () => {
		const locationParams = location.state;

		const queryParams = queryStringToObject();

		setParams({ ...locationParams, ...queryParams });
	};

	React.useEffect(() => {
		// load google reCAPTCHA script
		loadScriptByURL('recaptcha-key', function () {});
		checkLocationParams();
		setLoading(false);
	}, []);

	const login = async (values: AuthenticationValidateTwoFactorCommand, captchaToken: string) => {
		setLoading(true);

		if (params) {
			const response = await apibridge.postAuthenticationValidateTwoFactor({
				emailToken: params.token,
				token: values.token,
				captchaToken: captchaToken
			});

			if (response.isError) {
				setFormErrorMessage(
					response.validationErrors.length ? validationErrorsToString(response.validationErrors) : response.message
				);

				setLoading(false);
			} else if (response.result) {
				setLoading(false);

				dispatch(
					UserSlice.actions.update({
						childAwareId: response.result.childAwareId,
						email: response.result.email,
						firstName: response.result.firstName,
						lastName: response.result.lastName,
						loggedIn: true
					})
				);

				history.push({
					pathname: params.returnUrl ? params.returnUrl.split('?')[0] : '/dashboard',
					search: params.returnUrl ? params.returnUrl.split('?')[1] : '/dashboard',
					state: {
						organisationIdentifier: params.organisationIdentifier,
						paymentPlanId: params.paymentPlanId,
						profileType: params.profileType
					}
				});
			}
		} else {
			setLoading(false);
		}
	};

	const doLogin = async (values: AuthenticationValidateTwoFactorCommand) => {
		await window.grecaptcha.ready(() => {
			window.grecaptcha.execute(SITE_KEY, { action: 'submit' }).then((token: string) => {
				login(values, token);
			});
		});
	};

	const reSendCode = async (captchaToken: string) => {
		setLoading(true);

		if (params) {
			const response = await apibridge.postAuthenticationResendTwoFactorToken({
				emailToken: params.token,
				captchaToken: captchaToken
			});

			if (response.isError) {
				setFormErrorMessage(
					response.validationErrors.length ? validationErrorsToString(response.validationErrors) : response.message
				);

				setLoading(false);
			} else if (response.result) {
				setLoading(false);
			}
		}

		setLoading(false);
	};

	const resend = async () => {
		await window.grecaptcha.ready(() => {
			window.grecaptcha.execute(SITE_KEY, { action: 'submit' }).then((token: string) => {
				reSendCode(token);
			});
		});
	};

	return (
		<div className="two-factor-authentication">
			<SwitchTransition mode="out-in">
				<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
					{isLoading ? (
						<LoadingSpinner show={isLoading} />
					) : (
						<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={doLogin}>
							{(formik) => {
								return (
									<Form translate="yes">
										<div className="container">
											<div className="row">
												<div className="content col-12">
													<div className="header col-md-6 offset-md-3 form-group">
														<Twofa className="icon" />

														<h1 className="h2">Login code required</h1>

														<p className="sub-grey-text">
															An authentication code has been sent to{' '}
															<span className="email-text">{params && params.username ? params.username : ''}</span>
														</p>
													</div>

													<div className="form-group col-md-4 offset-md-4">
														<label htmlFor="token">Enter code</label>

														<Field name="token" id="token" type="text" className="form-control" />

														<ErrorMessage name="token" component="div" className="form-validation-error" />

														<p className="forgotten-links">
															I didn’t receive a code,{' '}
															<Button className="btn-text btn-inline" onClick={() => resend()}>
																Resend email
															</Button>
														</p>
													</div>

													<div className="actions col-md-4 offset-md-4">
														{!!formErrorMessage && <div className="form-validation-error">{formErrorMessage}</div>}

														<Button type="submit" className="btn-primary full-width" disabled={isLoading}>
															Log in
														</Button>
													</div>
												</div>
											</div>
										</div>
									</Form>
								);
							}}
						</Formik>
					)}
				</CSSTransition>
			</SwitchTransition>
		</div>
	);
};

export default TwoFactorAuthentication;
